import * as THREE from 'three';
import { TextSprite } from '../TextSprite';
import { v4 as uuid } from 'uuid';
import { ClipTask, DefaultColors } from '../defines';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import type { Subscription } from '@pointorama/potree-shared';
import { getFilterToggleProperty, getClipMethodProperty, getAnnotationVisibleProperty, getAnnotationColorProperty, getFillColorProperty, getBoxPositionProperty, getBoxScaleProperty, getBoxRotationProperty } from '@pointorama/potree-shared';

/** Map clip method string values to Potree ClipTask numbers */
function clipMethodToTask(clipMethod: string): number {
  switch (clipMethod) {
    case 'HIGHLIGHT':
      return ClipTask.GRAYSCALE;
    case 'HIDE':
      return ClipTask.SHOW_OUTSIDE;
    case 'ISOLATE':
      return ClipTask.SHOW_INSIDE;
    case 'HEATMAP':
      return ClipTask.HEATMAP;
    default:
      return ClipTask.NONE;
  }
}

type VolumeConstructorArgs = {
  clip?: boolean;
  modifiable?: boolean;
  identifier?: string;
  finished?: boolean;
  color?: THREE.Color | null;
  frameColor?: THREE.Color;
  visible?: boolean;
};

export class Volume extends THREE.Object3D {
  _clip: boolean;
  assignedClipTask: number = ClipTask.HIGHLIGHT;
  actualClipTask: number = ClipTask.HIGHLIGHT;
  useIntersection = false;
  _visible: boolean;
  showVolumeLabel: boolean;
  _modifiable: boolean;
  label: TextSprite;
  identifier: string;
  finished: boolean;
  color: THREE.Color;
  protected _frameColor: THREE.Color;
  constructor(args: VolumeConstructorArgs = {}) {
    super();

    if (this.constructor.name === 'Volume') {
      console.warn("Can't create object of class Volume directly. Use classes BoxVolume or SphereVolume instead.");
    }

    this._clip = args.clip || false;
    this._visible = true;
    this.visible = !!args.visible;
    this.showVolumeLabel = true;
    this._modifiable = args.modifiable || true;
    this.identifier = args.identifier || uuid();
    this.finished = args.finished || false;
    this.color = args.color || new THREE.Color(DefaultColors.HIGHLIGHT);
    this._frameColor = args.frameColor || DefaultColors.OBJECTS_THREE;

    this.label = new TextSprite('0');
    this.label.setBorderColor({ r: 0, g: 255, b: 0, a: 0.0 });
    this.label.setBackgroundColor({ r: 0, g: 255, b: 0, a: 0.0 });
    this.label.material.depthTest = false;
    this.label.material.depthWrite = false;
    this.label.material.transparent = true;
    this.label.position.y -= 0.5;
    this.add(this.label);

    this.label.updateMatrixWorld = () => {
      const volumeWorldPos = new THREE.Vector3();
      volumeWorldPos.setFromMatrixPosition(this.matrixWorld);
      this.label.position.copy(volumeWorldPos);
      this.label.updateMatrix();
      this.label.matrixWorld.copy(this.label.matrix);
      this.label.matrixWorldNeedsUpdate = false;

      for (let i = 0, l = this.label.children.length; i < l; i++) {
        this.label.children[i].updateMatrixWorld(true);
      }
    };
  }

  finish() {
    this.finished = true;
    this.assignedClipTask = ClipTask.NONE;
  }

  setClipTask(task: number) {
    if (task !== this.assignedClipTask) {
      this.assignedClipTask = task;
      this.actualClipTask = task;
    }
  }

  highlight() {
    if (this.assignedClipTask === ClipTask.NONE) {
      this.actualClipTask = ClipTask.HIGHLIGHT;
    }
  }

  unHighlight() {
    this.actualClipTask = this.assignedClipTask;
  }

  get isVisible() {
    return this._visible;
  }

  set isVisible(value) {
    if (this._visible !== value) {
      this._visible = value;
    }
  }

  getVolume() {
    console.warn('override this in subclass');
  }

  update() {
    // empty
  }

  raycast(_raycaster: THREE.Raycaster, _intersects: THREE.Intersection[]) {
    // empty
  }

  get clip() {
    return this._clip;
  }

  set clip(value) {
    if (this._clip !== value) {
      this._clip = value;

      this.update();
    }
  }

  get modifieable() {
    return this._modifiable;
  }

  set modifieable(value) {
    this._modifiable = value;

    this.update();
  }

  get frameColor(): THREE.Color {
    return this._frameColor;
  }

  set frameColor(value: THREE.Color) {
    this._frameColor = value;
  }
}

type BoxConstructorArgs = VolumeConstructorArgs & {
  position?: THREE.Vector3;
  scale?: THREE.Vector3;
  rotation?: THREE.Vector3;
  selected?: boolean;
};

export class BoxVolume extends Volume {
  material: THREE.MeshBasicMaterial;
  box: THREE.Mesh;
  boundingBox: THREE.Box3 | null;
  selectionFrame: Line2 = new Line2();
  frame: THREE.LineSegments = new THREE.LineSegments();
  boundingSphere: THREE.Sphere | undefined = undefined;
  selected = false;
  private _filterToggleSubscription?: Subscription;
  private _clipMethodSubscription?: Subscription;
  private _visibleSubscription?: Subscription;
  private _colorSubscription?: Subscription;
  private _frameColorSubscription?: Subscription;
  private _positionSubscription?: Subscription;
  private _scaleSubscription?: Subscription;
  private _rotationSubscription?: Subscription;
  /** Current filter enabled state (kept in sync by reactive subscription) */
  private _filterEnabled = false;
  /** Current clip method string (kept in sync by reactive subscription) */
  private _clipMethod = 'ISOLATE';

  constructor(args: BoxConstructorArgs = {}) {
    super(args);
    // @ts-expect-error: Property 'counter' does not exist on type 'Function'
    this.constructor.counter = this.constructor.counter === undefined ? 0 : this.constructor.counter + 1;
    // @ts-expect-error: Property 'counter' does not exist on type 'Function'
    this.name = 'box_' + this.constructor.counter;

    this.position.set(args.position?.x || 0, args.position?.y || 0, args.position?.z || 0);
    this.scale.set(args.scale?.x || 1, args.scale?.y || 1, args.scale?.z || 1);
    this.rotation.set(args.rotation?.x || 0, args.rotation?.y || 0, args.rotation?.z || 0);

    const boxGeometry = new THREE.BoxGeometry(1, 1, 1);
    boxGeometry.computeBoundingBox();

    this.material = new THREE.MeshBasicMaterial({
      color: new THREE.Color(DefaultColors.HIGHLIGHT),
      transparent: true,
      opacity: 0.3,
      depthTest: true,
      depthWrite: false,
    });
    this.box = new THREE.Mesh(boxGeometry, this.material);
    this.box.geometry.computeBoundingBox();
    this.boundingBox = this.box.geometry.boundingBox;
    this.add(this.box);

    // Separate frames for selected and unselected states, because thin lines render much nicer with buffer geometry
    this.createFrame();
    this.createSelectionFrame();

    if (args.selected) this.select();

    this.update();
  }

  createFrame() {
    const boxFrameGeometry = new THREE.BufferGeometry();

    const vertices = [];
    vertices.push(
      // bottom
      [-0.5, -0.5, 0.5],
      [0.5, -0.5, 0.5],
      [0.5, -0.5, 0.5],
      [0.5, -0.5, -0.5],
      [0.5, -0.5, -0.5],
      [-0.5, -0.5, -0.5],
      [-0.5, -0.5, -0.5],
      [-0.5, -0.5, 0.5],
      // top
      [-0.5, 0.5, 0.5],
      [0.5, 0.5, 0.5],
      [0.5, 0.5, 0.5],
      [0.5, 0.5, -0.5],
      [0.5, 0.5, -0.5],
      [-0.5, 0.5, -0.5],
      [-0.5, 0.5, -0.5],
      [-0.5, 0.5, 0.5],
      // sides
      [-0.5, -0.5, 0.5],
      [-0.5, 0.5, 0.5],
      [0.5, -0.5, 0.5],
      [0.5, 0.5, 0.5],
      [0.5, -0.5, -0.5],
      [0.5, 0.5, -0.5],
      [-0.5, -0.5, -0.5],
      [-0.5, 0.5, -0.5],
    );
    const vertices32 = new Float32Array(vertices.flat());
    boxFrameGeometry.setAttribute('position', new THREE.BufferAttribute(vertices32, 3));

    this.frame = new THREE.LineSegments(boxFrameGeometry, new THREE.LineBasicMaterial({ color: this.frameColor || DefaultColors.OBJECTS_THREE }));

    this.add(this.frame);
  }

  createSelectionFrame() {
    const lineVertices = [];
    lineVertices.push(
      [0.5, 0.5, 0.5],
      [0.5, -0.5, 0.5],
      [-0.5, -0.5, 0.5],
      [-0.5, 0.5, 0.5],
      [0.5, 0.5, 0.5],
      [0.5, 0.5, -0.5],
      [0.5, -0.5, -0.5],
      [-0.5, -0.5, -0.5],
      [-0.5, 0.5, -0.5],
      [0.5, 0.5, -0.5],

      [-0.5, 0.5, -0.5],
      [-0.5, 0.5, 0.5],
      [-0.5, -0.5, 0.5],
      [-0.5, -0.5, -0.5],
      [0.5, -0.5, -0.5],
      [0.5, -0.5, 0.5],
    );

    const selectedBoxFrameGeometry = new LineGeometry().setPositions(lineVertices.flat());
    const selectedBoxFrameMaterial = new LineMaterial({
      color: this.frameColor || DefaultColors.OBJECTS_THREE,
      linewidth: 3,
      resolution: new THREE.Vector2(1000, 1000),
      depthTest: true,
    });

    this.selectionFrame = new Line2(selectedBoxFrameGeometry, selectedBoxFrameMaterial);
    this.selectionFrame.visible = false;
    this.add(this.selectionFrame);
  }

  select() {
    this.selected = true;
    this.selectionFrame.visible = true;
    this.frame.visible = false;
  }

  unselect() {
    this.selected = false;
    this.selectionFrame.visible = false;
    this.frame.visible = true;
  }

  // Override frameColor setter to update the frame materials
  override get frameColor(): THREE.Color {
    return this._frameColor;
  }

  override set frameColor(value: THREE.Color) {
    this._frameColor = value;
    // Update both frame materials when color changes
    (this.frame.material as THREE.LineBasicMaterial).color = this._frameColor;
    (this.selectionFrame.material as LineMaterial).color = this._frameColor;
  }

  update() {
    this.boundingBox = this.box.geometry.boundingBox;
    this.boundingSphere = this.boundingBox?.getBoundingSphere(new THREE.Sphere());

    if (this._clip) {
      this.box.visible = false;
      this.label.visible = false;
    } else {
      this.box.visible = true;
      this.label.visible = this.showVolumeLabel;
    }
  }

  override raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]) {
    const is: THREE.Intersection[] = [];
    this.box.raycast(raycaster, is);

    if (is.length > 0) {
      const I = is[0];
      intersects.push({
        distance: I.distance,
        object: this,
        point: I.point.clone(),
      });
    }
  }

  getVolume() {
    return Math.abs(this.scale.x * this.scale.y * this.scale.z);
  }

  /**
   * Subscribe to filter toggle and clip method reactive properties.
   * When either changes, recomputes the clip task.
   * Call this after the box is added to the scene and the property service is initialized.
   */
  loadReactiveProperties(args: Partial<BoxConstructorArgs> = {}) {
    // --- Filter toggle + clip method (always read, never write) ---
    this._filterToggleSubscription?.unsubscribe();
    this._clipMethodSubscription?.unsubscribe();
    this._visibleSubscription?.unsubscribe();

    const toggleProperty = getFilterToggleProperty(this.identifier);
    if (toggleProperty) {
      this._filterToggleSubscription = toggleProperty.subscribe((enabled) => {
        this._filterEnabled = enabled;
        this._updateClipTask();
      });
    }

    const clipMethodProperty = getClipMethodProperty(this.identifier);
    if (clipMethodProperty) {
      this._clipMethodSubscription = clipMethodProperty.subscribe((method) => {
        this._clipMethod = method;
        this._updateClipTask();
      });
    }

    try {
      const visibleProperty = getAnnotationVisibleProperty(this.identifier);
      if (args.visible !== undefined) visibleProperty.value = this.visible;
      else this.visible = visibleProperty.value;
      this._visibleSubscription = visibleProperty.subscribeToChanges((value) => {
        this.visible = value;
      });

      // annotation_color → wireframe/outline (consistent across all annotation types)
      this._frameColorSubscription?.unsubscribe();
      const colorProp = getAnnotationColorProperty(this.identifier);
      if (args.color !== undefined) colorProp.value = '#' + this.color.getHexString();
      else this.frameColor = new THREE.Color(colorProp.value);
      this._frameColorSubscription = colorProp.subscribeToChanges((value: string) => {
        this.frameColor = new THREE.Color(value);
      });

      // fill_color → solid fill material (optional, for boxes/planes/surfaces)
      this._colorSubscription?.unsubscribe();
      const fillColorProp = getFillColorProperty(this.identifier);
      if (args.frameColor !== undefined) fillColorProp.value = '#' + this.frameColor.getHexString();
      else {
        this.color = new THREE.Color(fillColorProp.value);
        this.material.color = this.color;
      }
      this._colorSubscription = fillColorProp.subscribeToChanges((value: string) => {
        this.color = new THREE.Color(value);
        this.material.color = this.color;
      });

      // Geometry properties (position, scale, rotation)
      // Skip the initial emission only if the box already has non-default geometry
      // (i.e., data was passed via constructor). If the box was created empty (PK-836 sync),
      // accept the first value from the property system.
      const hasPosition = this.position.x !== 0 || this.position.y !== 0 || this.position.z !== 0;
      const hasScale = this.scale.x !== 1 || this.scale.y !== 1 || this.scale.z !== 1;
      const hasRotation = this.rotation.x !== 0 || this.rotation.y !== 0 || this.rotation.z !== 0;

      this._positionSubscription?.unsubscribe();
      const posProp = getBoxPositionProperty(this.identifier);
      let posInit = false;
      this._positionSubscription = posProp.subscribe((value) => {
        if (hasPosition && !posInit) { posInit = true; return; }
        posInit = true;
        this.position.set(value.x, value.y, value.z);
      });

      this._scaleSubscription?.unsubscribe();
      const scaleProp = getBoxScaleProperty(this.identifier);
      let scaleInit = false;
      this._scaleSubscription = scaleProp.subscribe((value) => {
        if (hasScale && !scaleInit) { scaleInit = true; return; }
        scaleInit = true;
        this.scale.set(value.x, value.y, value.z);
      });

      this._rotationSubscription?.unsubscribe();
      const rotProp = getBoxRotationProperty(this.identifier);
      let rotInit = false;
      this._rotationSubscription = rotProp.subscribe((value) => {
        if (hasRotation && !rotInit) { rotInit = true; return; }
        rotInit = true;
        this.rotation.set(value.x, value.y, value.z);
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  private _updateClipTask() {
    const task = this._filterEnabled ? clipMethodToTask(this._clipMethod) : ClipTask.NONE;
    this.setClipTask(task);
  }

  dispose() {
    this._filterToggleSubscription?.unsubscribe();
    this._filterToggleSubscription = undefined;
    this._clipMethodSubscription?.unsubscribe();
    this._clipMethodSubscription = undefined;
    this._visibleSubscription?.unsubscribe();
    this._visibleSubscription = undefined;
    this._colorSubscription?.unsubscribe();
    this._colorSubscription = undefined;
    this._frameColorSubscription?.unsubscribe();
    this._frameColorSubscription = undefined;
  }

  getActualClipTask() {
    if (!this.visible) return this.assignedClipTask;
    if (this.assignedClipTask === ClipTask.GRAYSCALE || this.assignedClipTask === ClipTask.SHOW_INSIDE || this.assignedClipTask === ClipTask.SHOW_OUTSIDE) {
      return this.assignedClipTask;
    } else if (this.selected || this.actualClipTask === ClipTask.HIGHLIGHT) {
      return ClipTask.HIGHLIGHT;
    } else {
      return ClipTask.NONE;
    }
  }
}

export class SphereVolume extends Volume {
  material: THREE.MeshBasicMaterial;
  sphere: THREE.Mesh;
  boundingBox: THREE.Box3 | null;
  frame: THREE.LineSegments | THREE.Mesh;
  boundingSphere: THREE.Sphere | undefined = undefined;
  constructor(args = {}) {
    super(args);

    // @ts-expect-error: Property 'counter' does not exist on type 'Function'
    this.constructor.counter = this.constructor.counter === undefined ? 0 : this.constructor.counter + 1;
    // @ts-expect-error: Property 'counter' does not exist on type 'Function'
    this.name = 'sphere_' + this.constructor.counter;

    const sphereGeometry = new THREE.SphereGeometry(1, 32, 32);
    sphereGeometry.computeBoundingBox();

    this.material = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.3,
      depthTest: true,
      depthWrite: false,
    });
    this.sphere = new THREE.Mesh(sphereGeometry, this.material);
    this.sphere.visible = false;
    this.sphere.geometry.computeBoundingBox();
    this.boundingBox = this.sphere.geometry.boundingBox;
    this.add(this.sphere);

    this.label.visible = false;

    const frameGeometry = new THREE.BufferGeometry();
    {
      const steps = 64;
      const uSegments = 8;
      const vSegments = 5;

      const vertices = [];
      for (let uSegment = 0; uSegment < uSegments; uSegment++) {
        const alpha = (uSegment / uSegments) * Math.PI * 2;
        const dirx = Math.cos(alpha);
        const diry = Math.sin(alpha);

        for (let i = 0; i <= steps; i++) {
          const v = (i / steps) * Math.PI * 2;
          const vNext = v + (2 * Math.PI) / steps;

          const height = Math.sin(v);
          const xyAmount = Math.cos(v);

          const heightNext = Math.sin(vNext);
          const xyAmountNext = Math.cos(vNext);

          const vertex = new THREE.Vector3(dirx * xyAmount, diry * xyAmount, height);
          vertices.push(vertex.x, vertex.y, vertex.z);

          const vertexNext = new THREE.Vector3(dirx * xyAmountNext, diry * xyAmountNext, heightNext);
          vertices.push(vertexNext.x, vertexNext.y, vertexNext.z);
        }
      }

      // creates rings at poles, just because it's easier to implement
      for (let vSegment = 0; vSegment <= vSegments + 1; vSegment++) {
        let uh = vSegment / (vSegments + 1); // -1 to 1
        uh = (1 - uh) * (-Math.PI / 2) + uh * (Math.PI / 2);
        const height = Math.sin(uh);

        console.log(uh, height);

        for (let i = 0; i <= steps; i++) {
          const u = (i / steps) * Math.PI * 2;
          const uNext = u + (2 * Math.PI) / steps;

          const dirx = Math.cos(u);
          const diry = Math.sin(u);

          const dirxNext = Math.cos(uNext);
          const diryNext = Math.sin(uNext);

          const xyAmount = Math.sqrt(1 - height * height);

          const vertex = new THREE.Vector3(dirx * xyAmount, diry * xyAmount, height);
          vertices.push(vertex.x, vertex.y, vertex.z);

          const vertexNext = new THREE.Vector3(dirxNext * xyAmount, diryNext * xyAmount, height);
          vertices.push(vertexNext.x, vertexNext.y, vertexNext.z);
        }
        frameGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
      }
    }

    this.frame = new THREE.LineSegments(frameGeometry, new THREE.LineBasicMaterial({ color: 0x000000 }));
    this.add(this.frame);

    const frameMaterial = new THREE.MeshBasicMaterial({ wireframe: true, color: 0x000000 });
    this.frame = new THREE.Mesh(sphereGeometry, frameMaterial);
    //this.add(this.frame);

    //this.frame = new THREE.LineSegments(boxFrameGeometry, new THREE.LineBasicMaterial({color: 0x000000}));
    // this.frame.mode = THREE.Lines;
    //this.add(this.frame);

    this.update();
  }

  update() {
    this.boundingBox = this.sphere.geometry.boundingBox;
    this.boundingSphere = this.boundingBox?.getBoundingSphere(new THREE.Sphere()) ?? new THREE.Sphere();

    //if (this._clip) {
    //	this.sphere.visible = false;
    //	this.label.visible = false;
    //} else {
    //	this.sphere.visible = true;
    //	this.label.visible = this.showVolumeLabel;
    //}
  }

  override raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]) {
    const is: THREE.Intersection[] = [];
    this.sphere.raycast(raycaster, is);

    if (is.length > 0) {
      const I = is[0];
      intersects.push({
        distance: I.distance,
        object: this,
        point: I.point.clone(),
      });
    }
  }

  // see https://en.wikipedia.org/wiki/Ellipsoid#Volume
  getVolume() {
    return (4 / 3) * Math.PI * this.scale.x * this.scale.y * this.scale.z;
  }
}
